import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileExport } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import AliasFilters from './alias/AliasFilters';
import AliasTable from './alias/AliasTable';
import CreateAliasDialog from './alias/CreateAliasDialog';
import EditAliasDialog from './alias/EditAliasDialog';
import ConfirmationModal from './shared/ConfirmationModal';
import { useAliases } from '../hooks/useAliases';
import { useAliasFilters } from '../hooks/useAliasFilters';

const AliasManagementPage = ({ customerId }) => {
  // Custom hooks for data and filtering
  const {
    aliases,
    customers,
    loading,
    error,
    success,
    selectedCustomerId,
    selectedFieldKey,
    setSelectedCustomerId,
    setSelectedFieldKey,
    createAlias,
    updateAlias,
    deactivateAlias,
    exportAliases,
  } = useAliases();

  const {
    filteredAliases,
    searchTerm,
    setSearchTerm,
  } = useAliasFilters(aliases);

  // Set the customer ID from prop when provided
  useEffect(() => {
    if (customerId && customerId !== selectedCustomerId) {
      setSelectedCustomerId(customerId);
    }
  }, [customerId, selectedCustomerId, setSelectedCustomerId]);

  // Modal states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [aliasToEdit, setAliasToEdit] = useState(null);
  const [aliasToDeactivate, setAliasToDeactivate] = useState(null);

  // Handlers
  const handleCreateAlias = () => {
    setShowCreateDialog(true);
  };

  const handleSaveAlias = async (aliasData) => {
    try {
      await createAlias(aliasData);
      setShowCreateDialog(false);
    } catch (err) {
      // Error already handled in hook
    }
  };

  const handleCancelCreate = () => {
    setShowCreateDialog(false);
  };

  const handleEditAlias = (alias) => {
    setAliasToEdit(alias);
    setShowEditDialog(true);
  };

  const handleSaveEdit = async (aliasId, updateData) => {
    try {
      await updateAlias(aliasId, updateData);
      setShowEditDialog(false);
      setAliasToEdit(null);
    } catch (err) {
      // Error already handled in hook
    }
  };

  const handleCancelEdit = () => {
    setShowEditDialog(false);
    setAliasToEdit(null);
  };

  const handleDeactivateAlias = (alias) => {
    setAliasToDeactivate(alias);
  };

  const confirmDeactivateAlias = async () => {
    try {
      await deactivateAlias(aliasToDeactivate.id);
      setAliasToDeactivate(null);
    } catch (err) {
      // Error already handled in hook
      setAliasToDeactivate(null);
    }
  };

  const handleExportAliases = async () => {
    try {
      await exportAliases();
    } catch (err) {
      // Error already handled in hook
    }
  };

  return (
    <div className="user-list">
      <div className="user-list-header">
        <h2>Alias Management</h2>
        <div>
          <button 
            onClick={handleExportAliases} 
            className="btn btn-outline-primary me-2"
            disabled={!selectedCustomerId || loading || aliases.length === 0}
            title="Export aliases to JSON"
          >
            <FontAwesomeIcon icon={faFileExport} /> Export to JSON
          </button>
          <button 
            onClick={handleCreateAlias} 
            className="btn btn-primary"
            disabled={!selectedCustomerId || loading}
          >
            <FontAwesomeIcon icon={faPlus} /> Create Alias
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      <AliasFilters
        customers={customerId ? [] : customers}
        selectedCustomerId={selectedCustomerId}
        selectedFieldKey={selectedFieldKey}
        searchTerm={searchTerm}
        onCustomerChange={customerId ? null : setSelectedCustomerId}
        onFieldKeyChange={setSelectedFieldKey}
        onSearchChange={setSearchTerm}
        loading={loading}
        hideCustomerFilter={!!customerId}
      />

      {loading && !customerId && customers.length === 0 ? (
        <div>Loading customers...</div>
      ) : !selectedCustomerId ? (
        <div className="alert alert-info">
          {customerId ? 'Loading aliases...' : 'Please select a customer to view aliases.'}
        </div>
      ) : loading ? (
        <div>Loading aliases...</div>
      ) : filteredAliases.length === 0 ? (
        <div className="alert alert-info">
          {searchTerm 
            ? 'No aliases match your search criteria.'
            : 'No aliases found. Create your first alias to get started.'}
        </div>
      ) : (
        <AliasTable
          aliases={filteredAliases}
          onEdit={handleEditAlias}
          onDeactivate={handleDeactivateAlias}
        />
      )}

      {/* Create Alias Dialog */}
      {showCreateDialog && (
        <CreateAliasDialog
          show={showCreateDialog}
          customerId={selectedCustomerId}
          onSave={handleSaveAlias}
          onCancel={handleCancelCreate}
        />
      )}

      {/* Edit Alias Dialog */}
      {showEditDialog && (
        <EditAliasDialog
          show={showEditDialog}
          alias={aliasToEdit}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
        />
      )}

      {/* Deactivate Confirmation Modal */}
      <ConfirmationModal
        show={!!aliasToDeactivate}
        title="Confirm Deactivate"
        message={
          <>
            <p>Are you sure you want to deactivate this alias?</p>
            <div className="mt-2">
              <strong>Field Key:</strong> <code>{aliasToDeactivate?.fieldKey}</code>
              <br />
              <strong>Original Value:</strong> <code>{aliasToDeactivate?.originalValue}</code>
              <br />
              <strong>Replacement Value:</strong> <code>{aliasToDeactivate?.replacementValue}</code>
            </div>
            <p className="text-muted mt-2">
              This alias will no longer be applied to future lookups. This action can be reversed by creating a new alias.
            </p>
          </>
        }
        confirmText="Deactivate Alias"
        cancelText="Cancel"
        confirmVariant="danger"
        onConfirm={confirmDeactivateAlias}
        onCancel={() => setAliasToDeactivate(null)}
      />
    </div>
  );
};

AliasManagementPage.propTypes = {
  customerId: PropTypes.string,
};

AliasManagementPage.defaultProps = {
  customerId: null,
};

export default AliasManagementPage;
